import axios from 'axios';
import { APIEndpoint } from 'common/enum';
import { universalPutRequest } from 'common/requestHandler';
import { FileData } from 'common/utils';
import {
  allowedFileTypes,
  defaultErrorMessage,
  largeFileTypes,
  MAX_FILE_SIZE_MB,
} from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';

// Define the Tag interface
interface Tag {
  name: string;
  value: string;
}
export const handleFileUpload = async (
  uuid: string | undefined,
  folderId: string | null,
  firstName: string | null,
  lastName: string | null,
  uploadedFiles: File[],
  newFolderPath: string,
  setModalOpen: (isOpen: boolean) => void,
  setUploadedFiles: (files: File[]) => void,
  setUploading: (uploading: boolean) => void,
  setProgress: React.Dispatch<
    React.SetStateAction<{
      [key: string]: number;
    }>
  >,
  accessToken: string | null
) => {
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  setUploading(true);

  const uploadPromises = uploadedFiles.map(async (file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      popupNotification(
        `File ${file.name} is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`,
        false
      );
      return; // Skip uploading this file
    }
    if (
      !allowedFileTypes.includes(file.type) &&
      !largeFileTypes.includes(file.type)
    ) {
      popupNotification(`File ${file.name} is not a valid file type.`, false);
      return; // Skip uploading this file
    }
    try {
      const { data } = await axios.post(
        APIEndpoint.uploadFile,
        {
          fileName: `${uuid}/${folderId}/Home/${
            newFolderPath.length > 0 ? newFolderPath + '/' : ''
          }${file.name}`,
          uploadedBy: firstName + ' ' + lastName,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken,
          },
        }
      );

      const uploadUrl = data.upload_url.url;

      const formData = new FormData();
      if (data.upload_url.fields) {
        Object.entries(data.upload_url.fields).forEach(([k, v]: any) => {
          formData.append(k, v);
        });
      } else {
        console.error('No fields found in the response');
      }

      formData.append('file', file);

      await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total ?? progressEvent.loaded;
          const progressPercentage = Math.round(
            (progressEvent.loaded / total) * 100
          );
          setProgress((prevProgress: { [key: string]: number }) => ({
            ...prevProgress,
            [file.name]: progressPercentage,
          }));
        },
      });
      popupNotification('Files uploaded successfully', true);
    } catch (error) {
      popupNotification(defaultErrorMessage, false);
    }
  });

  await Promise.all(uploadPromises).then(() => {
    // popupNotification('Files uploaded successfully', true);
    setModalOpen(false);
    setUploadedFiles([]);
    setUploading(false);
  });
};

// Function to rename a file
export const renameFile = async (
  sourcePath: string,
  destinationPath: string,
  deleteOldVersions: boolean = false
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const payload = {
      deleteOldVersions: deleteOldVersions.toString(),
      sourcePath,
      destinationPath,
      actionName: 'LAW77D8QL',
      httpMethod: 'PUT',
    };

    const response = await universalPutRequest(
      APIEndpoint.secDocsMain,
      payload
    );

    if ('error' in response) {
      return {
        success: false,
        error: response.error,
      };
    } else {
      return {
        success: true,
        message: response.message || 'File renamed successfully',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: 'Failed to rename file',
    };
  }
};

/**
 * Search files and folders by text (current level only)
 * @param items - Array of files and folders to search
 * @param searchTerm - Search term to filter by
 * @returns Filtered array of files and folders
 */
export const searchFilesAndFolders = (
  items: FileData[],
  searchTerm: string
): FileData[] => {
  if (!searchTerm.trim()) {
    return items;
  }

  const term = searchTerm.toLowerCase();
  return items.filter((item) => {
    // Check basic fields
    const basicMatch =
      item.name.toLowerCase().includes(term) ||
      (item['uploaded-by']?.toLowerCase() || '').includes(term) ||
      (item['scan-status']?.toLowerCase() || '').includes(term) ||
      (item.size?.toLowerCase() || '').includes(term);

    // Check tags if they exist (handle both possible structures)
    const tags = (item as any).tags || [];

    const tagMatch =
      tags.some((tag: any) => {
        // Skip tags with invalid values like "None"
        if (
          tag.Key === 'None' ||
          tag.Value === 'None' ||
          !tag.Key ||
          !tag.Value
        ) {
          return false;
        }
        const keyMatch = tag.Key?.toLowerCase().includes(term);
        const valueMatch = tag.Value?.toLowerCase().includes(term);
        return keyMatch || valueMatch;
      }) || false;

    return basicMatch || tagMatch;
  });
};

/**
 * Recursively search files and folders by text (includes nested folders)
 * @param items - Array of files and folders to search
 * @param searchTerm - Search term to filter by
 * @param currentPath - Current path for building full paths
 * @returns Array of search results with full paths
 */
export const searchFilesAndFoldersRecursive = (
  items: FileData[],
  searchTerm: string,
  currentPath: string[] = []
): Array<FileData & { fullPath: string[] }> => {
  if (!searchTerm.trim()) {
    return [];
  }

  const term = searchTerm.toLowerCase();
  const results: Array<FileData & { fullPath: string[] }> = [];

  const searchInItems = (itemsToSearch: FileData[], path: string[]) => {
    itemsToSearch.forEach((item) => {
      // Check if current item matches search
      const basicMatch =
        item.name.toLowerCase().includes(term) ||
        (item['uploaded-by']?.toLowerCase() || '').includes(term) ||
        (item['scan-status']?.toLowerCase() || '').includes(term) ||
        (item.size?.toLowerCase() || '').includes(term);

      // Check tags if they exist (handle both possible structures)
      const tags = (item as any).tags || [];

      const tagMatch =
        tags.some((tag: any) => {
          // Skip tags with invalid values like "None"
          if (
            tag.Key === 'None' ||
            tag.Value === 'None' ||
            !tag.Key ||
            !tag.Value
          ) {
            return false;
          }
          const keyMatch = tag.Key?.toLowerCase().includes(term);
          const valueMatch = tag.Value?.toLowerCase().includes(term);
          return keyMatch || valueMatch;
        }) || false;

      const matchesSearch = basicMatch || tagMatch;

      if (matchesSearch) {
        results.push({
          ...item,
          fullPath: [...path, item.name],
        });
      }

      // Recursively search in nested folders
      if (item.type === 'folder' && item.items && item.items.length > 0) {
        searchInItems(item.items, [...path, item.name]);
      }
    });
  };

  searchInItems(items, currentPath);
  return results;
};

/**
 * Enhanced tag search function with better matching
 * @param item - File or folder item
 * @param searchTerm - Search term to match against
 * @returns True if tags match the search term
 */
export const searchInTags = (item: FileData, searchTerm: string): boolean => {
  const term = searchTerm.toLowerCase();
  const tags = (item as any).tags || [];

  if (!tags.length) return false;

  return tags.some((tag: any) => {
    // Handle both tag structures
    const tagKey = tag.Key || tag.name || '';
    const tagValue = tag.Value || tag.value || '';

    // Skip tags with invalid values like "None"
    if (tagKey === 'None' || tagValue === 'None' || !tagKey || !tagValue) {
      return false;
    }

    // Check for exact matches, partial matches, and contains
    const keyMatch = tagKey.toLowerCase().includes(term);
    const valueMatch = tagValue.toLowerCase().includes(term);

    // Also check if search term contains tag key/value (reverse search)
    const reverseKeyMatch = term.includes(tagKey.toLowerCase());
    const reverseValueMatch = term.includes(tagValue.toLowerCase());

    return keyMatch || valueMatch || reverseKeyMatch || reverseValueMatch;
  });
};
